/**
 * RecentAnalysesFeed — Section header + GlassCard wrapper containing the
 * AnalysisFeedRow list.
 *
 * GlassCard padding is overridden to 0 so AnalysisFeedRow controls its own
 * horizontal/vertical padding.
 *
 * "View All" link colour is #7C3AED (purple-700, 5.9:1 on white — WCAG AA).
 */

import React from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  FlatList,
} from 'react-native';
import type { TFunction } from '@dramadetector/shared';
import { GlassCard } from '../../glass/GlassCard';
import { AnalysisFeedRow } from './AnalysisFeedRow';

export interface RecentAnalysisSummary {
  id: string;
  clientName: string;
  clientInitials: string;
  avatarIndex: number;
  patternKey: string;
  date: Date;
  dramaScore: number;
}

interface RecentAnalysesFeedProps {
  analyses: RecentAnalysisSummary[];
  onViewAll: () => void;
  onAnalysisPress: (id: string) => void;
  isLoading?: boolean;
  t: TFunction<'practitioner'>;
  locale: string;
}

function formatDate(date: Date, locale: string): string {
  try {
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  } catch {
    return date.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  }
}

function ItemSeparator(): React.ReactElement {
  return <View style={styles.separator} />;
}

export const RecentAnalysesFeed: React.FC<RecentAnalysesFeedProps> = ({
  analyses,
  onViewAll,
  onAnalysisPress,
  isLoading = false,
  t,
  locale,
}) => {
  return (
    <View style={styles.container}>
      {/* Section header */}
      <View style={styles.headerRow}>
        <Text style={styles.sectionTitle}>
          {t('sections.recent_analyses')}
        </Text>
        <Pressable
          onPress={onViewAll}
          accessibilityRole="link"
          accessibilityLabel={t('accessibility.view_all_analyses')}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Text style={styles.viewAll}>{t('sections.view_all')}</Text>
        </Pressable>
      </View>

      {/* Feed card — override padding to 0 */}
      <GlassCard withChromeBorder={false} intensity={40} noPadding style={styles.feedCard}>
        {isLoading ? (
          <ActivityIndicator
            color="#7C3AED"
            style={styles.loader}
          />
        ) : analyses.length === 0 ? (
          <Text style={styles.emptyText}>{t('sections.no_analyses')}</Text>
        ) : (
          <FlatList
            data={analyses}
            scrollEnabled={false}
            keyExtractor={(item) => item.id}
            ItemSeparatorComponent={ItemSeparator}
            renderItem={({ item }) => (
              <AnalysisFeedRow
                id={item.id}
                clientName={item.clientName}
                clientInitials={item.clientInitials}
                avatarIndex={item.avatarIndex}
                patternLabel={t(`patterns.${item.patternKey}` as any)}
                formattedDate={formatDate(item.date, locale)}
                dramaScore={item.dramaScore}
                onPress={onAnalysisPress}
                t={t}
              />
            )}
          />
        )}
      </GlassCard>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#18181B',
  },
  viewAll: {
    fontSize: 12,
    fontWeight: '600',
    color: '#7C3AED',
  },
  feedCard: {
    overflow: 'hidden',
  },
  loader: {
    paddingVertical: 24,
  },
  separator: {
    height: 1,
    backgroundColor: 'rgba(0,0,0,0.06)',
    marginHorizontal: 16,
  },
  emptyText: {
    paddingVertical: 24,
    paddingHorizontal: 16,
    fontSize: 14,
    color: '#A1A1AA',
    textAlign: 'center',
  },
});
